import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { getRequest, postRequest, baseURL } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import MyEditor from '../../components/Editor';
import { BellSlashIcon, DeleteIcon } from '../svg/svgIcons';
import { crmCommunicationTypeLabelKey, isKnownCrmCommunicationType, renderCrmCommunicationTypeIcon } from '../../utils/crmCommunicationTypeIcons';
import { getPusherChannel } from '../../utils/pusherChannel';
import '../../assets/css/support.css';
import CrmCommunicationApprovalsPanel from '../../components/crm/communication/CrmCommunicationApprovalsPanel';
import CrmCommunicationInspectionPanel from '../../components/crm/communication/CrmCommunicationInspectionPanel';
import CrmCommunicationInterestedPartiesPanel from '../../components/crm/communication/CrmCommunicationInterestedPartiesPanel';
import CrmCommunicationExecutionDeadlinePanel from '../../components/crm/communication/CrmCommunicationExecutionDeadlinePanel';
import CrmCommunicationParticipantsPanel from '../../components/crm/communication/CrmCommunicationParticipantsPanel';
import CrmCommunicationNextStepPanel from '../../components/crm/communication/CrmCommunicationNextStepPanel';
import CrmTaskTableLauncher from '../../components/crm/communication/CrmTaskTableLauncher';
import CrmCommunicationActivityPanel from '../../components/crm/communication/CrmCommunicationActivityPanel';
import { CrmAnnouncementRecipientsDetailSection } from '../../components/crm/communication/CrmCommunicationRecipientsPanel';
import {
  enrichCrmTicketForListStatusDisplay,
  workflowCrmStatesForTicketType,
} from '../../utils/crmCommunicationTicketStatus';
import { CrmTicketListStatusCell } from '../../components/crm/communication/CrmTicketListCells';
import {
  findCrmReopenStateId,
  findCrmStornoStateId,
  isCrmWorkflowClosedState,
} from '../../utils/crmCommunicationWorkflowStateIds';
import { displayCrmStateName } from '../../utils/crmStateI18n';
import { crmLevelTypeAliasesForTicketType, filterCrmWorkflowStatesByPhase } from '../../utils/crmCommunicationTypeAliases';
import { hasCrmModuleAccess, hasCrmModuleEdit, hasFpCrmEdit } from '../../utils/crmCommunicationPermissions';
import { canUserEditCrmCommunicationDeadline, isUserInCrmLevel } from '../../utils/crmCommunicationDeadlineAccess';
import { canUserSetCrmNextStepAssignee } from '../../utils/crmCommunicationAssigneeAccess';
import { selectActiveCrmSteps, reloadCrmStatesCatalog, reloadCrmStepsCatalog } from '../../utils/crmCatalogStore';
import {
  buildSemaphoreStateOptions,
  semaphoreColorFromState,
  semaphoreLabelFromState,
} from '../../utils/crmCommunicationSemaphoreStates';
import { showCrmWorkflowToast } from '../../utils/crmCommunicationPanelSave';

const FULL_INCLUDE = 'recipients,participants,departments,acquisition_state,quote_state,comments,approvals,attachments,task_table,events';

function storagePublicHref(path) {
  if (!path) return '';
  const p = String(path).replace(/^\/+/, '');
  return `${baseURL}/storage/${encodeURI(p)}`;
}

function userDisplayName(u) {
  return u?.employee?.name || u?.username || '';
}

/**
 * @param {{ ticketId: number, portalView?: boolean }} props
 */
export default function CrmCommunicationTicketDetail({ ticketId, portalView = false }) {
  const { t, locale } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast, getUserColor, getUserInitials, getDateFormat, formatDate, openFilePreview, setDialogText, setVisible } = useContext(UtilContext);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [savingDetail, setSavingDetail] = useState(false);
  const [taskTableSummary, setTaskTableSummary] = useState(null);
  const [attachmentItems, setAttachmentItems] = useState(null);
  const [acquisitionStateId, setAcquisitionStateId] = useState('');
  const [quoteStateId, setQuoteStateId] = useState('');
  const [savingSemaphoreState, setSavingSemaphoreState] = useState(false);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const lastLocalUpdateAtRef = useRef(0);
  const readMarkedRef = useRef(false);

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const crmStepsCatalog = useMemo(() => selectActiveCrmSteps(state), [state.crm_steps]);

  const loadTicket = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    if (!ticketId) return;
    if (!silent) {
      setLoading(true);
      setLoadError(null);
      setAttachmentItems(null);
    }
    try {
      const qs = `include=${encodeURIComponent(FULL_INCLUDE)}`;
      const res = await getRequest(`/api/crm_communication_tickets/${ticketId}?${qs}`);
      if (res?.status === 200 && res?.data) {
        setTicket(res.data);
        if (!readMarkedRef.current) {
          readMarkedRef.current = true;
          void postRequest(`/api/crm_communication_tickets/${ticketId}/read`, {}, true)
            .then(() => {
              dispatch({ type: 'updateCrmTicketViewed', payload: Number(ticketId), value: false });
            })
            .catch(() => {});
        }
      } else if (!silent) {
        setTicket(null);
        setLoadError(t('CrmCommunication.Detail.NotFound'));
      }
    } catch (e) {
      if (!silent) {
        setTicket(null);
        setLoadError(String(e?.message || t('CrmCommunication.Detail.NotFound')));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [ticketId, t, dispatch]);

  /** Po akcii v paneloch – bez skeletonu (parita ServiceTaskDetail). */
  const refreshTicket = useCallback(async () => {
    lastLocalUpdateAtRef.current = Date.now();
    await loadTicket({ silent: true });
    if (ticketId) {
      dispatch({ type: 'updateCrmTicketViewed', payload: Number(ticketId), value: false });
    }
  }, [dispatch, loadTicket, ticketId]);

  /** Pravý stĺpec: overlay + reload hlavičky (stav, semafory, …) – parita FP setDialogText/setVisible. */
  const onPanelChanged = useCallback(async (saveFn) => {
    if (typeof setDialogText === 'function') {
      setDialogText(t('Common.Saving'));
    }
    if (typeof setVisible === 'function') {
      setVisible(true);
    }
    try {
      lastLocalUpdateAtRef.current = Date.now();
      let saveResult = null;
      if (typeof saveFn === 'function') {
        saveResult = await saveFn();
      }
      const ticketData = saveResult?.data ?? (saveResult?.id != null ? saveResult : null);
      const isTicketPayload = ticketData
        && ticketData.id != null
        && (
          ticketData.ticket_code != null
          || ticketData.type != null
          || ticketData.title != null
          || Array.isArray(ticketData.approval)
        );
      if (isTicketPayload) {
        setTicket((prev) => (
          prev && Number(prev.id) === Number(ticketData.id)
            ? { ...prev, ...ticketData }
            : ticketData
        ));
      } else {
        await loadTicket({ silent: true });
      }
      // Parita ServiceTaskDetail updateTaskViewed: po vlastnej úprave list hneď ukáže prečítané.
      if (ticketId) {
        dispatch({ type: 'updateCrmTicketViewed', payload: Number(ticketId), value: false });
      }
    } finally {
      if (typeof setVisible === 'function') {
        setVisible(false);
      }
    }
  }, [dispatch, loadTicket, setDialogText, setVisible, t, ticketId]);

  useEffect(() => {
    readMarkedRef.current = false;
    void loadTicket();
  }, [loadTicket]);

  useEffect(() => {
    if (!Array.isArray(state?.crm_states) || state.crm_states.length === 0) {
      void reloadCrmStatesCatalog(dispatch, getRequest).catch(() => {});
    }
    if (!Array.isArray(state?.crm_steps) || state.crm_steps.length === 0) {
      void reloadCrmStepsCatalog(dispatch, getRequest).catch(() => {});
    }
  }, [dispatch, state?.crm_states, state?.crm_steps]);

  const acquisitionSemaphoreDefs = useMemo(
    () => filterCrmWorkflowStatesByPhase(state?.crm_states, 'semaphore', 'acquisition'),
    [state?.crm_states],
  );
  const quoteSemaphoreDefs = useMemo(
    () => filterCrmWorkflowStatesByPhase(state?.crm_states, 'semaphore', 'quote_request'),
    [state?.crm_states],
  );

  const attachmentItemsKey = useMemo(() => {
    const att = Array.isArray(ticket?.attachments) ? ticket.attachments : [];
    return att.map((a) => String(a?.id ?? '')).join(',');
  }, [ticket?.attachments]);

  useEffect(() => {
    if (!ticket?.id) return;
    setAttachmentItems(null);
    const att = Array.isArray(ticket.attachments) ? ticket.attachments : [];
    if (att.length === 0) {
      setAttachmentItems([]);
      return;
    }
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        setAttachmentItems(
          att.map((a) => {
            const sp = String(a.storage_path || '');
            return {
              id: a.id,
              href: storagePublicHref(sp),
              filename: sp.split('/').pop() || sp,
              createdByUserId: a.created_by_user_id,
            };
          }),
        );
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [ticket?.id, attachmentItemsKey]);

  useEffect(() => {
    setAcquisitionStateId(ticket?.current_acq_crm_state_id != null ? String(ticket.current_acq_crm_state_id) : '');
  }, [ticket?.current_acq_crm_state_id]);

  useEffect(() => {
    setQuoteStateId(ticket?.current_qte_crm_state_id != null ? String(ticket.current_qte_crm_state_id) : '');
  }, [ticket?.current_qte_crm_state_id]);

  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel || !ticketId) return;
    const onUpdated = (payload) => {
      if (Number(payload?.ticket_id) !== Number(ticketId)) return;
      if (Date.now() - lastLocalUpdateAtRef.current < 2000) return;
      void loadTicket({ silent: true });
    };
    const onDeleted = (payload) => {
      if (Number(payload?.ticket_id) !== Number(ticketId)) return;
      dispatch({ type: 'close_tab', id: state.selectedTab });
    };
    channel.bind('crm_communication_ticket.updated', onUpdated);
    channel.bind('crm_communication_ticket.created', onUpdated);
    channel.bind('crm_communication_ticket.deleted', onDeleted);
    return () => {
      channel.unbind('crm_communication_ticket.updated', onUpdated);
      channel.unbind('crm_communication_ticket.created', onUpdated);
      channel.unbind('crm_communication_ticket.deleted', onDeleted);
    };
  }, [dispatch, loadTicket, state.selectedTab, ticketId]);

  const usersById = useMemo(() => {
    const map = new Map();
    (state?.users || []).forEach((u) => map.set(Number(u.id), u));
    return map;
  }, [state?.users]);

  const creator = useMemo(
    () => usersById.get(Number(ticket?.created_by_user_id)) || null,
    [ticket?.created_by_user_id, usersById],
  );

  const workflowLocked = !!ticket?.workflow_locked;
  const approvalRequired = !!ticket?.approval_required;
  const approvalInProgress = !!ticket?.approval_in_progress;
  const phasePermissions = ticket?.phase_permissions || {};
  const isAnnouncement = ticket?.type === 'announcement';
  const isAcquisition = ticket?.type === 'acquisition';
  const isQuote = ticket?.type === 'quote_request';
  const showWorkflowPanels = !isAnnouncement;
  const sem = isAcquisition ? ticket?.current_acquisition_state : isQuote ? ticket?.current_quote_state : null;
  const semTicketType = isAcquisition ? 'acquisition' : 'quote_request';
  const semLabel = (isAcquisition || isQuote)
    ? semaphoreLabelFromState(sem, semTicketType, t, locale, displayCrmStateName)
    : '';
  const semColor = (isAcquisition || isQuote) ? semaphoreColorFromState(sem, semTicketType, sem?.color) : sem?.color;
  const ticketForStatusDisplay = useMemo(
    () => enrichCrmTicketForListStatusDisplay(ticket, state?.crm_states ?? [], crmStepsCatalog, locale),
    [ticket, state?.crm_states, crmStepsCatalog, locale],
  );
  const workflowStatusOptions = useMemo(
    () => workflowCrmStatesForTicketType(state?.crm_states ?? [], ticket?.type)
      .map((s) => ({
        value: Number(s.id),
        label: displayCrmStateName(s.name, locale),
      }))
      .filter((o) => o.label && !Number.isNaN(o.value)),
    [state?.crm_states, ticket?.type, locale],
  );
  const canEditTicket = portalView ? hasFpCrmEdit(user) : hasCrmModuleEdit(user);
  const moduleAccessDenied = !portalView && !hasCrmModuleAccess(user);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin'
    || !!user?.is_superuser
    || (Array.isArray(user?.roles) && user.roles.some((r) => String(r?.name || r).toLowerCase() === 'admin'));
  const stornoStateId = useMemo(() => findCrmStornoStateId(state?.crm_states ?? []), [state?.crm_states]);
  const reopenStateId = useMemo(() => findCrmReopenStateId(state?.crm_states ?? []), [state?.crm_states]);
  const currentWorkflowState = useMemo(
    () => (state?.crm_states ?? []).find((s) => Number(s.id) === Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? 0)),
    [state?.crm_states, ticket?.status_id, ticket?.status_crm_state_id],
  );
  const isClosedWorkflow = isCrmWorkflowClosedState(currentWorkflowState);
  const isClosingFinalAuto = String(currentWorkflowState?.phase ?? '') === 'closing'
    && String(currentWorkflowState?.transition_type ?? '') === 'final_auto';
  const isClosedOrTerminal = !!ticket?.cancelled_at || !!ticket?.is_terminal;
  const canEditAcquisitionState = isAcquisition
    && (Number(ticket?.created_by_user_id) === Number(user?.id) || isAdmin)
    && !isClosedOrTerminal;
  const canEditQuoteState = isQuote
    && (Number(ticket?.created_by_user_id) === Number(user?.id) || isAdmin)
    && !isClosedOrTerminal;
  const acquisitionStateOptions = useMemo(
    () => buildSemaphoreStateOptions(acquisitionSemaphoreDefs, 'acquisition', t),
    [acquisitionSemaphoreDefs, t],
  );
  const quoteStateOptions = useMemo(
    () => buildSemaphoreStateOptions(quoteSemaphoreDefs, 'quote_request', t),
    [quoteSemaphoreDefs, t],
  );
  const acquisitionStateLabel = useMemo(() => {
    if (semLabel) return semLabel;
    const selected = acquisitionStateOptions.find((opt) => opt.value === acquisitionStateId);
    return selected?.label || '';
  }, [acquisitionStateId, acquisitionStateOptions, semLabel]);
  const acquisitionBadgeColor = semaphoreColorFromState(
    acquisitionSemaphoreDefs.find((row) => Number(row?.id) === Number(acquisitionStateId)) || sem,
    'acquisition',
    semColor,
  );
  const quoteStateLabel = useMemo(() => {
    if (semLabel) return semLabel;
    const selected = quoteStateOptions.find((opt) => opt.value === quoteStateId);
    return selected?.label || '';
  }, [quoteStateId, quoteStateOptions, semLabel]);
  const quoteBadgeColor = semaphoreColorFromState(
    quoteSemaphoreDefs.find((row) => Number(row?.id) === Number(quoteStateId)) || sem,
    'quote_request',
    semColor,
  );
  const semaphoreLabel = isAcquisition ? acquisitionStateLabel : (isQuote ? quoteStateLabel : '');
  const semaphoreBadgeColor = isAcquisition ? acquisitionBadgeColor : (isQuote ? quoteBadgeColor : semColor);
  const canEditSemaphoreState = isAcquisition ? canEditAcquisitionState : (isQuote ? canEditQuoteState : false);
  const participantRows = Array.isArray(ticket?.participants) ? ticket.participants : [];
  const departmentRows = Array.isArray(ticket?.departments) ? ticket.departments : [];
  const involvedUserRow = useMemo(
    () => participantRows.find((row) => Boolean(row?.involved) || String(row?.role || '').toLowerCase() === 'involved') || null,
    [participantRows],
  );
  const involvedDepartmentId = involvedUserRow
    ? null
    : (departmentRows.find((row) => Boolean(row?.involved))?.department_id != null
      ? Number(departmentRows.find((row) => Boolean(row?.involved))?.department_id)
      : null);
  const watcherParticipantRows = useMemo(
    () => participantRows,
    [participantRows],
  );
  const myParticipant = participantRows.find((row) => Number(row?.user_id) === Number(user?.id)) || null;
  const notificationEnabled = myParticipant ? Boolean(myParticipant.notification) : false;
  const executionPanelsFrozen = workflowLocked || approvalInProgress;
  const nextStepLockedDuringApproval = approvalInProgress && !workflowLocked;
  const canEditParticipants = !workflowLocked && !!phasePermissions.participants && !ticket?.cancelled_at;
  const executionLockedByPlanner = (isAcquisition || isQuote) && taskTableSummary != null && taskTableSummary.done !== true;
  const canManagePlanner = showWorkflowPanels
    && !workflowLocked
    && !ticket?.cancelled_at
    && (
      Number(user?.id) === Number(ticket?.created_by_user_id)
      || (state?.crm_levels || [])
        .filter((lvl) => String(lvl?.task || '') === 'management'
          && crmLevelTypeAliasesForTicketType(ticket?.type).includes(String(lvl?.type || '')))
        .some((lvl) => isUserInCrmLevel(state, user, lvl.id))
    );

  const canEditDeadline = useMemo(
    () => canUserEditCrmCommunicationDeadline(ticket, user, state, workflowLocked),
    [ticket, user, state, workflowLocked],
  );
  const canSetNextStepAssignee = useMemo(
    () => canUserSetCrmNextStepAssignee(ticket, user, state, workflowLocked, approvalInProgress),
    [ticket, user, state, workflowLocked, approvalInProgress],
  );

  const markLocalUpdate = useCallback(() => {
    lastLocalUpdateAtRef.current = Date.now();
  }, []);

  const isTicketFounder = Number(user?.id) === Number(ticket?.created_by_user_id);
  const canEditFounderTicketDetail = isTicketFounder && !isClosingFinalAuto;
  const canEditDetailTitle = canEditFounderTicketDetail;
  const canEditDetailBody = canEditFounderTicketDetail && approvalInProgress;
  const showDetailSaveButton = canEditDetailTitle || canEditDetailBody;

  const detailFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      theme: ticket?.title || '',
      description: ticket?.body_html || '',
    },
    onSubmit: async (values) => {
      if (!ticketId || savingDetail || !showDetailSaveButton) return;
      setSavingDetail(true);
      try {
        if (typeof setDialogText === 'function') setDialogText(t('Common.Saving'));
        if (typeof setVisible === 'function') setVisible(true);
        const payload = {};
        if (canEditDetailTitle) payload.title = values.theme;
        if (canEditDetailBody) payload.body_html = values.description;
        const res = await postRequest(`/api/crm_communication_tickets/update/${ticketId}`, payload, true);
        if (res?.status === 200 && res?.data) {
          setTicket(res.data);
          markLocalUpdate();
          toast?.current?.show({
            severity: 'info',
            summary: t('ServiceTask.Confirmed'),
            detail: t('ServiceTaskDetail.Toast.RecordSaved'),
            life: 3000,
          });
          setShowDetail(false);
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      } catch (err) {
        toast?.current?.show({
          severity: 'error',
          summary: t('CrmCommunication.Toast.Error'),
          detail: String(err?.message || err),
          life: 4000,
        });
      } finally {
        setSavingDetail(false);
        if (typeof setVisible === 'function') setVisible(false);
      }
    },
  });

  const saveSemaphoreState = useCallback(async (nextStateId = null) => {
    if (!ticketId || !(isAcquisition || isQuote) || !canEditSemaphoreState) return;
    try {
      setSavingSemaphoreState(true);
      const currentValue = isAcquisition ? acquisitionStateId : quoteStateId;
      const payloadValue = nextStateId != null ? String(nextStateId) : currentValue;
      const payload = isAcquisition
        ? { current_acq_crm_state_id: payloadValue ? Number(payloadValue) : null }
        : { current_qte_crm_state_id: payloadValue ? Number(payloadValue) : null };
      await onPanelChanged(async () => {
        const res = await postRequest(`/api/crm_communication_tickets/update/${ticketId}`, payload, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.StateSaved');
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setSavingSemaphoreState(false);
    }
  }, [acquisitionStateId, canEditSemaphoreState, isAcquisition, isQuote, onPanelChanged, quoteStateId, t, ticketId, toast]);

  const changeStatus = useCallback(async (value, { confirmMessage } = {}) => {
    if (!ticketId || value == null || value === '') return;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    try {
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      const formData = new FormData();
      formData.append('status_id', String(value));
      const res = await postRequest(`/api/crm_communication_tickets/updateStatus/${ticketId}`, formData, true);
      if (res?.status === 200 && res?.data) {
        lastLocalUpdateAtRef.current = Date.now();
        setTicket(res.data);
        toast?.current?.show({
          severity: 'info',
          summary: t('ServiceTask.Confirmed'),
          life: 3000,
          detail: t('ServiceTaskDetail.Toast.TaskStatusUpdated'),
        });
      } else {
        throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setVisible?.(false);
    }
  }, [setDialogText, setVisible, t, ticketId, toast]);

  const changeNotification = useCallback(async () => {
    if (!ticketId || notificationBusy || ticket?.cancelled_at) return;
    try {
      setNotificationBusy(true);
      const nextValue = notificationEnabled ? 0 : 1;
      await onPanelChanged(async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/update_notification`, { value: nextValue }, true);
        if (res?.status === 200) {
          toast?.current?.show({
            severity: 'info',
            summary: t('ServiceTask.Confirmed'),
            life: 2500,
            detail: notificationEnabled
              ? t('ServiceTaskDetail.Toast.NotificationsOff')
              : t('ServiceTaskDetail.Toast.NotificationsOn'),
          });
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setNotificationBusy(false);
    }
  }, [notificationBusy, notificationEnabled, onPanelChanged, t, ticket?.cancelled_at, ticketId, toast]);

  const removeAttachment = useCallback(async (attachmentId) => {
    if (!ticketId || !attachmentId) return;
    if (!window.confirm(t('ServiceTask.DeleteFilePrompt'))) return;
    try {
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      lastLocalUpdateAtRef.current = Date.now();
      const res = await fetch(`${baseURL}/api/crm_communication_tickets/${ticketId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.FileDeleted');
      await refreshTicket();
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setVisible?.(false);
    }
  }, [baseURL, refreshTicket, setDialogText, setVisible, t, ticketId, toast]);

  function renderTicketType(type) {
    if (!isKnownCrmCommunicationType(type)) return null;
    const typeKey = crmCommunicationTypeLabelKey(type);
    const typeTitle = t(typeKey);
    return (
      <div className="flex items-center gap-2 text-gray-800">
        {renderCrmCommunicationTypeIcon(type, { width: 8, height: 8, title: typeTitle })}
        <span>{typeTitle}</span>
      </div>
    );
  }

  if (!ticketId) {
    return <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4"><Message severity="warn" text={t('CrmCommunication.Detail.NoId')} /></div>;
  }

  if (moduleAccessDenied) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <Message severity="warn" text={t('CrmCommunication.Detail.ModuleAccessDenied')} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <div className="w-auto bg-white rounded-md shadow-md px-10 py-6">
          <Skeleton height="2rem" className="mb-2" />
          <Skeleton height="4rem" className="mb-2" />
          <Skeleton height="12rem" />
        </div>
      </div>
    );
  }

  if (loadError || !ticket) {
    return (
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <Message severity="warn" text={loadError || t('CrmCommunication.Detail.NotFound')} />
      </div>
    );
  }

  const creatorName = userDisplayName(creator) || `#${ticket.created_by_user_id}`;
  const createdAt = ticket.created_at ? DateTime.fromISO(String(ticket.created_at)) : null;
  const traderName = ticket.primary_company?.contractor_name ? String(ticket.primary_company.contractor_name) : '';
  const traderUsername = ticket.primary_company?.contractor_username ? String(ticket.primary_company.contractor_username) : '';
  const traderAssignedText = ticket.primary_company?.trader_assigned_from
    ? (formatDate
      ? formatDate(ticket.primary_company.trader_assigned_from, dateTimeFmt, true)
      : DateTime.fromISO(String(ticket.primary_company.trader_assigned_from)).toFormat(dateTimeFmt))
    : '';

  return (
    <>
      <div className="relative flex flex-col bg-gray-100 w-full py-4 px-4">
        <div className="w-auto bg-white rounded-md shadow-md px-10 py-6 h-[calc(100vh_-_170px)]">
          <div className="flex font-bold text-sm text-blue-800">
            {t('Menu.CRM.Communication')} / {ticket.ticket_code}
          </div>
          <div className="flex flex-row gap-11">
            <div className="basis-2/3 min-w-0">
              <div className="text-2xl font-light text-black py-6">{ticket.title}</div>
              <div className="flex flex-row justify-between items-center w-full p-4 border border-zinc-600 rounded-sm mb-2">
                <div className="flex items-center flex-wrap gap-1">
                  <Avatar
                    className="p-overlay-badge"
                    title={creatorName}
                    label={getUserInitials(creatorName)}
                    shape="circle"
                    style={{ backgroundColor: getUserColor(creator?.username ?? ''), color: '#ffffff', marginRight: '5px' }}
                  />
                  {creatorName} {t('ServiceTaskDetail.CreatedBy')}
                  {createdAt ? <> {createdAt.toFormat(dateFmt)}{t('Common.DateTimeAt')}{createdAt.toFormat('HH:mm')}</> : null}
                </div>
                <button type="button" onClick={() => setShowDetail(true)} className="font-semibold">{t('ServiceTaskDetail.Detail')}</button>
              </div>
              <div className="max-h-[calc(100vh_-_450px)] overflow-y-auto flex-1 min-w-0">
                {showWorkflowPanels ? (
                  <CrmTaskTableLauncher
                    crmTicketId={ticketId}
                    getRequest={getRequest}
                    postRequest={postRequest}
                    employees={state?.users || []}
                    canManage={canManagePlanner}
                    onSummaryChange={setTaskTableSummary}
                    taskTheme={ticket?.title}
                    taskDescription={ticket?.body_html}
                    taskSenderId={ticket?.created_by_user_id}
                    crmTicketType={ticket?.type}
                  />
                ) : null}
                <CrmCommunicationActivityPanel
                  ticketId={ticketId}
                  ticket={ticket}
                  ticketType={ticket.type}
                  usersById={usersById}
                  dateFmt={dateFmt}
                  dateTimeFmt={dateTimeFmt}
                  onChanged={onPanelChanged}
                  onTicketUpdated={(data) => {
                    setTicket(data);
                    markLocalUpdate();
                  }}
                  onLocalUpdate={markLocalUpdate}
                  canComment={!ticket.cancelled_at}
                />
                {attachmentItems === null && (Array.isArray(ticket?.attachments) && ticket.attachments.length > 0) ? (
                  <Skeleton height="4rem" className="mb-2" />
                ) : null}
                {attachmentItems && attachmentItems.length > 0 ? (
                  <div>
                    <h2 className="text-xl">{t('CrmCommunication.Attachments.Title')}</h2>
                    <div className="flex flex-row flex-wrap w-full h-full border border-separate border-gray-400 rounded-md bg-white mb-4">
                      {attachmentItems.map((a) => (
                          <div key={String(a.id)} className="text-black h-auto w-52">
                            <div className="flex flex-row p-2">
                              <div role="button" tabIndex={0} onClick={() => openFilePreview?.({ url: a.href, filename: a.filename })} onKeyDown={() => {}}>
                                <embed src={`${a.href}#page=1&view=FitH&scrollbar=0`} width="100px" height="auto" title={a.filename} />
                              </div>
                              <div className="flex flex-col ml-4">
                                <button type="button" className="text-left" style={{ color: 'blue', cursor: 'pointer', marginBottom: '2px' }} onClick={() => openFilePreview?.({ url: a.href, filename: a.filename })}>{a.filename}</button>
                                {(user?.role === 'admin' || Number(a.createdByUserId) === Number(user?.id)) ? (
                                  <button
                                    type="button"
                                    className="w-fit mt-1 flex flex-row px-1 py-1 border rounded-md bg-red-900"
                                    onClick={() => void removeAttachment(a.id)}
                                  >
                                    <DeleteIcon />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="basis-1/3 h-[calc(100vh_-_270px)] overflow-y-auto overflow-x-hidden">
              <div className="flex flex-col mb-3">
                <label className="font-bold mb-0">{t('ServiceTaskTable.Col.Status')}</label>
                <div className="text-gray-800 mb-3 mt-1 flex items-center gap-2 flex-wrap">
                  {!isAdmin || !canEditTicket ? (
                    <CrmTicketListStatusCell
                      rowData={ticketForStatusDisplay}
                      crmStates={state?.crm_states ?? []}
                      locale={locale}
                    />
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      <select
                        value={Number(ticket?.status_id ?? ticket?.status_crm_state_id ?? '') || ''}
                        onChange={(e) => void changeStatus(parseInt(e.target.value, 10))}
                        className="block w-60 px-4 py-2 mt-1 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                      >
                        <option value="">{t('Common.Dash')}</option>
                        {workflowStatusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {ticket.cancelled_at ? <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">{t('CrmCommunication.Detail.CancelledBadge')}</span> : null}
                </div>
                <div className="flex flex-col mb-3">
                  <label className="font-bold mb-1">{t('CrmCommunication.List.Type')}</label>
                  <div className="flex flex-col items-start gap-2">
                    {renderTicketType(ticket.type)}
                    {(isAcquisition || isQuote) ? (
                      <div className="flex flex-col gap-1 w-full">
                        <label className="font-bold mb-1">{t('CustomerCentralDatabaseList.Col.Trader')}</label>
                        <div className="flex items-center gap-2 text-gray-800">
                          {ticket.primary_company?.contractor_id ? (
                            <>
                              <Avatar
                                className="shrink-0"
                                title={traderName}
                                label={getUserInitials(traderName)}
                                shape="circle"
                                style={{
                                  backgroundColor: getUserColor(traderUsername || traderName),
                                  color: '#ffffff',
                                }}
                              />
                              <span className="min-w-0 truncate" title={traderAssignedText || undefined}>
                                {traderAssignedText || t('Common.Dash')}
                              </span>
                            </>
                          ) : (
                            <span>{t('Common.Dash')}</span>
                          )}
                        </div>
                      </div>
                    ) : null}
                    {(isAcquisition || isQuote) ? (
                      <div className="flex flex-col gap-2 w-full">
                        <label className="font-bold mb-1">{isAcquisition ? t('CrmCommunication.Catalog.AcqState') : t('CrmCommunication.Catalog.QteState')}</label>
                        {canEditSemaphoreState ? (
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: semaphoreBadgeColor || '#6b7280' }}
                            />
                            <Dropdown
                              value={isAcquisition ? acquisitionStateId : quoteStateId}
                              disabled={savingSemaphoreState}
                              onChange={(e) => {
                                const next = e.value || '';
                                if (isAcquisition) setAcquisitionStateId(next);
                                else setQuoteStateId(next);
                                void saveSemaphoreState(next);
                              }}
                              options={(isAcquisition ? acquisitionStateOptions : quoteStateOptions).map((opt) => ({
                                label: opt.label,
                                value: opt.value,
                              }))}
                              placeholder={t('CrmCommunication.Catalog.None')}
                              className="w-60 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                            />
                          </div>
                        ) : (
                          <span className="flex items-center gap-2 text-base text-gray-600">
                            <span
                              className="inline-block w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: semaphoreBadgeColor || '#6b7280' }}
                            />
                            <span>{semaphoreLabel || t('Common.Dash')}</span>
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {isAnnouncement ? (
                <div className="mb-3 text-gray-800">
                  <span className="font-bold">{t('ServiceTaskDetail.LastSent')}: </span>
                  {ticket.last_sent_at
                    ? DateTime.fromISO(String(ticket.last_sent_at)).toFormat(dateTimeFmt)
                    : t('ServiceTaskDetail.NoSendRecord')}
                </div>
              ) : null}

              {isAnnouncement && ticket.scheduled_send_at ? (
                <div className="mb-3 text-gray-800">
                  {t('CrmCommunication.Detail.ScheduledSend')}: {DateTime.fromISO(String(ticket.scheduled_send_at)).toFormat(dateTimeFmt)}
                </div>
              ) : null}

              <div className="mb-3">
                <button
                  type="button"
                  className="flex flex-row gap-2 disabled:opacity-25"
                  onClick={() => void changeNotification()}
                  disabled={
                    notificationBusy
                    || !!ticket.cancelled_at
                    || (isClosedWorkflow && String(currentWorkflowState?.transition_type ?? '') === 'final_auto')
                  }
                >
                  {notificationEnabled ? <BellSlashIcon /> : <i title={t('ServiceTask.Notifications')} className="pi pi-bell" style={{ fontSize: '1.4rem', marginRight: 5 }} />}
                  <span className="text-gray-800">{notificationEnabled ? t('ServiceTaskDetail.TurnOffNotifications') : t('ServiceTaskDetail.TurnOnNotifications')}</span>
                </button>
              </div>

              <div className="mb-3">
                <button
                  type="button"
                  className="flex flex-row gap-2 disabled:opacity-25"
                  disabled={
                    Number(user?.id) !== Number(ticket?.created_by_user_id)
                    || !stornoStateId
                    || isClosedWorkflow
                    || Number(ticket?.status_id ?? ticket?.status_crm_state_id) === Number(stornoStateId)
                    || !!ticket?.cancelled_at
                  }
                  onClick={() => void changeStatus(stornoStateId, { confirmMessage: t('CrmCommunication.Detail.CancelConfirm') })}
                >
                  <i title={t('ServiceTaskDetail.StornoTicket')} className="pi pi-ban" style={{ fontSize: '1.4rem', marginRight: 5 }} />
                  <span className="text-gray-800">{t('ServiceTaskDetail.StornoTicket')}</span>
                </button>
              </div>
              {!isAnnouncement ? (
                <div className="mb-3">
                  <button
                    type="button"
                    className="flex flex-row gap-2 disabled:opacity-25"
                    disabled={!isClosingFinalAuto || !reopenStateId}
                    onClick={() => void changeStatus(reopenStateId)}
                  >
                    <i title={t('ServiceTask.Reopen')} className="pi pi-replay" style={{ fontSize: '1.4rem', marginRight: 5 }} />
                    <span className="text-gray-800">{t('ServiceTask.Reopen')}</span>
                  </button>
                </div>
              ) : null}

              {approvalRequired ? (
                <CrmCommunicationApprovalsPanel
                  embedded
                  ticketId={ticketId}
                  ticket={ticket}
                  ticketType={ticket.type}
                  approvalRequests={ticket.approval_requests}
                  onChanged={onPanelChanged}
                  onCloseTicket={(stateId) => void changeStatus(stateId)}
                  readOnly={workflowLocked || !phasePermissions.approvals}
                />
              ) : null}
              {showWorkflowPanels ? (
                <CrmCommunicationParticipantsPanel
                  embedded
                  ticketId={ticketId}
                  ticketType={ticket.type}
                  participants={participantRows}
                  departments={departmentRows}
                  onChanged={onPanelChanged}
                  readOnly={!canEditParticipants}
                />
              ) : null}
              {(isAcquisition || isQuote) ? (
                <CrmCommunicationNextStepPanel
                  embedded
                  ticketId={ticketId}
                  ticket={ticket}
                  ticketType={ticket.type}
                  execution={ticket.execution}
                  crmSteps={crmStepsCatalog}
                  onChanged={onPanelChanged}
                  onCloseTicket={(stateId) => void changeStatus(stateId)}
                  readOnlyAssignee={workflowLocked || nextStepLockedDuringApproval || !phasePermissions.next_step || !canSetNextStepAssignee}
                  readOnlyStep={workflowLocked || nextStepLockedDuringApproval || !phasePermissions.next_step || !canSetNextStepAssignee}
                  readOnlyExecutorState={executionPanelsFrozen}
                  executionLocked={executionLockedByPlanner}
                  approvalInProgress={approvalInProgress}
                  executionPhaseAllowed={!!phasePermissions.execution}
                />
              ) : null}
              {(isAcquisition || isQuote) ? (
                <CrmCommunicationInspectionPanel
                  embedded
                  ticketId={ticketId}
                  ticket={ticket}
                  ticketType={ticket.type}
                  inspection={ticket.inspection}
                  onChanged={onPanelChanged}
                  onCloseTicket={(stateId) => void changeStatus(stateId)}
                  readOnly={workflowLocked || !phasePermissions.inspection}
                  workflowLocked={workflowLocked}
                  approvalInProgress={approvalInProgress}
                />
              ) : null}
              {(isAcquisition || isQuote) ? (
                <CrmCommunicationExecutionDeadlinePanel
                  ticketId={ticketId}
                  execution={ticket.execution}
                  onChanged={onPanelChanged}
                  disabled={!canEditDeadline}
                />
              ) : null}

              <CrmCommunicationInterestedPartiesPanel
                ticketId={ticketId}
                ticket={ticket}
                ticketType={ticket.type}
                departmentRows={departmentRows}
                watcherParticipantRows={watcherParticipantRows}
                involvedDepartmentId={involvedDepartmentId}
                onChanged={onPanelChanged}
                readOnly={!canEditParticipants}
                getUserColor={getUserColor}
                getUserInitials={getUserInitials}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog header={t('ServiceTaskDetail.TicketDetail')} visible={showDetail} style={{ width: '70vw' }} onHide={() => setShowDetail(false)}>
        <form onSubmit={detailFormik.handleSubmit} className="w-auto flex flex-col">
          <div className="mb-2">
            <label className="font-semibold text-sm">{t('ServiceTaskDetail.Name')}:</label>
            <input
              name="theme"
              required
              disabled={!canEditDetailTitle || savingDetail}
              value={detailFormik.values.theme}
              onChange={detailFormik.handleChange}
              className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
          </div>
          <div className="mb-2">
            <label className="font-semibold text-sm">{t('ServiceTaskDetail.Description')}:</label>
            {canEditDetailBody ? (
              <MyEditor editorValue={detailFormik.values.description} autofocus fileAccept="*" formik={detailFormik} />
            ) : (
              <MyEditor editorValue={detailFormik.values.description || ticket.body_html || ''} hideToolbar hideFileUpload formik={null} />
            )}
          </div>
          {(ticket.primary_company_name || ticket.primary_contact_name || (ticket.submitter_same_as_sender && ticket.created_by_name)) ? (
            <div className="mb-2">
              <label className="font-semibold text-sm">{t('CrmCommunication.Form.Field.PrimaryCustomer')} / {t('CrmCommunication.Form.Field.Buyer')}:</label>
              <input
                readOnly
                value={[ticket.primary_company_name, ticket.primary_contact_name || (ticket.submitter_same_as_sender ? ticket.created_by_name : null)].filter(Boolean).join(' — ')}
                className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md"
              />
            </div>
          ) : null}
          {ticket.submitter_same_as_sender != null ? (
            <div className="mb-2 text-sm text-gray-700">
              {t('CrmCommunication.Detail.SubmitterSame')}: {ticket.submitter_same_as_sender ? t('Common.Yes') : t('Common.No')}
            </div>
          ) : null}
          {isAnnouncement ? (
            <CrmAnnouncementRecipientsDetailSection
              recipients={ticket.recipients}
              lastSentAt={ticket.last_sent_at}
              dateTimeFmt={dateTimeFmt}
            />
          ) : null}
          {showDetailSaveButton ? (
            <button
              type="submit"
              disabled={savingDetail}
              className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25"
            >
              {t('Common.Save')}
            </button>
          ) : null}
        </form>
      </Dialog>
    </>
  );
}
